export interface Tool {
  id: string
  name: string
  description: string
  bestFor: string
  pricing: 'Free' | 'Freemium' | 'Paid'
  ease: 'Beginner' | 'Pro'
  category: 'Writing' | 'Design' | 'Video' | 'Code' | 'Audio' | 'Research'
  url: string
  trending?: boolean
  featured?: boolean
  isNew?: boolean
  tags?: string[]
  useCases?: string[]
  longDescription?: string
  rating?: number
}

export const tools: Tool[] = [
  {
    id: 'myndfield-ai',
    name: 'Myndfield.AI',
    description:
      'An institutional-grade AI decision engine that transforms complex data into clear, actionable intelligence for high-stakes decision making.',
    bestFor: 'Solo operators',
    pricing: 'Paid',
    ease: 'Pro',
    category: 'Research',
    url: 'https://myndfield.ai',
    trending: true,
    featured: true,
    isNew: true,
    rating: 5.0,
    tags: [
      'decision intelligence',
      'institutional grade',
      'strategic analysis',
      'enterprise AI',
      'exclusive',
    ],
    useCases: [
      'Analyse multi-variable scenarios to make confident strategic decisions',
      'Synthesise market data, research and internal signals into a single decision framework',
      'Run institutional-quality risk assessments without a team of analysts',
      'Generate board-ready insights and recommendations in minutes',
    ],
    longDescription:
      'Myndfield.AI is not another productivity tool — it is an institutional-grade decision intelligence engine built for leaders, founders and operators who cannot afford to get it wrong. Designed with the rigour of hedge fund analytics and the clarity of world-class consulting, Myndfield.AI ingests complex, multi-source data and distils it into decisive, actionable intelligence. Whether you are evaluating a market entry, assessing portfolio risk or navigating a high-stakes negotiation, Myndfield.AI gives you the analytical firepower that was previously reserved for institutions with seven-figure research budgets. Exclusive access. Unmatched clarity. Decisions that move the needle.',
  },
  {
    id: 'notebooklm',
    name: 'NotebookLM',
    description:
      "Google's AI-powered research and note-taking tool that lets you upload sources and have conversations with your documents",
    bestFor: 'Students',
    pricing: 'Free',
    ease: 'Beginner',
    category: 'Research',
    url: 'https://notebooklm.google.com',
    featured: true,
    isNew: true,
    tags: ['research', 'note-taking', 'study', 'documents'],
    useCases: [
      'Upload lecture PDFs and ask questions to study for exams',
      'Summarise long research papers into key takeaways',
      'Create audio overviews of your documents to listen on the go',
      'Compare multiple sources to find contradictions or patterns',
    ],
    longDescription:
      'NotebookLM is a free AI research assistant from Google that lets you upload PDFs, Google Docs, websites, and YouTube videos as sources, then have natural conversations with your material. It generates summaries, answers questions grounded in your sources, and can even create audio overviews — podcast-style discussions of your documents. Ideal for students preparing for exams, researchers synthesising literature, or professionals digesting lengthy reports. Unlike general chatbots, every response is grounded in your uploaded sources, reducing hallucinations and making it easy to verify claims.',
    rating: 4.5,
  },
  {
    id: 'claude',
    name: 'Claude',
    description:
      "Anthropic's AI assistant for writing, analysis, coding, and thoughtful conversation",
    bestFor: 'Solo operators',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Writing',
    url: 'https://claude.ai',
    featured: true,
    trending: true,
    tags: ['writing', 'analysis', 'coding', 'conversation'],
    useCases: [
      'Draft and refine professional emails or cover letters',
      'Analyse spreadsheet data and get plain-English insights',
      'Debug code and get step-by-step explanations',
      'Brainstorm content ideas with back-and-forth conversation',
    ],
    longDescription:
      'Claude is an AI assistant built by Anthropic that excels at long-form writing, nuanced analysis, and thoughtful conversation. It can handle documents up to 200,000 tokens — entire books or codebases — making it uniquely suited for deep research and complex reasoning tasks. Claude is known for following detailed instructions carefully, producing well-structured outputs, and being transparent about its limitations. Whether you need to draft a business proposal, analyse a dataset, debug code, or brainstorm creative ideas, Claude provides thorough, well-reasoned responses.',
    rating: 4.8,
  },
  {
    id: 'studyfetch',
    name: 'StudyFetch',
    description:
      'AI-powered study platform that turns your notes and lectures into flashcards, quizzes, and personalised study plans',
    bestFor: 'Students',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Research',
    url: 'https://studyfetch.com',
    isNew: true,
    tags: ['study', 'flashcards', 'quizzes', 'learning'],
    useCases: [
      'Turn lecture recordings into flashcard sets',
      'Generate practice quizzes from your notes',
      'Create personalised study schedules before exams',
      "Get AI explanations for concepts you don't understand",
    ],
    longDescription:
      'StudyFetch is an AI-powered study platform designed specifically for students. Upload your lecture notes, slides, or textbook chapters and it automatically generates flashcards, practice quizzes, and personalised study plans. The platform adapts to your learning pace, focusing more on areas where you struggle and less on what you already know. It supports multiple study modes including spaced repetition, active recall testing, and AI-powered explanations that break down complex concepts into digestible pieces.',
    rating: 4.3,
  },
  {
    id: '1',
    name: 'Notion AI',
    description: 'AI-powered writing assistant built into your workspace',
    bestFor: 'Teams',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Writing',
    url: 'https://notion.so',
    trending: true,
    featured: true,
    tags: ['writing', 'productivity', 'workspace', 'notes'],
    useCases: [
      'Auto-summarise meeting notes into action items',
      'Generate first drafts of project briefs from bullet points',
      'Translate documents for international team members',
      'Extract key dates and deadlines from long documents',
    ],
    longDescription:
      'Notion AI is deeply integrated into the Notion workspace, bringing AI capabilities directly into your notes, docs, wikis, and project management tools. It can summarise meeting notes, generate first drafts from bullet points, translate content into dozens of languages, and extract action items from lengthy documents. Because it lives inside Notion, it understands the context of your workspace — your projects, databases, and team knowledge — making its suggestions more relevant than standalone AI tools. Perfect for teams who already use Notion and want to supercharge their existing workflows.',
    rating: 4.7,
  },
  {
    id: '2',
    name: 'Runway',
    description: 'Generate and edit video with AI models',
    bestFor: 'Creators',
    pricing: 'Freemium',
    ease: 'Pro',
    category: 'Video',
    url: 'https://runwayml.com',
    trending: true,
    isNew: true,
    tags: ['video-editing', 'video-generation', 'vfx'],
    useCases: [
      'Remove backgrounds from product shots in seconds',
      'Generate B-roll footage from text descriptions',
      'Extend video clips with AI-generated frames',
      'Apply cinematic colour grading with one click',
    ],
    longDescription:
      'Runway is a professional-grade AI video platform used by filmmakers, content creators, and creative studios worldwide. Its Gen-3 Alpha model can generate video clips from text descriptions, extend existing footage, remove or replace backgrounds, apply style transfers, and perform advanced editing tasks that traditionally required expensive software and hours of manual work. Runway also offers tools for image generation, audio cleanup, and motion tracking. While it has a learning curve for advanced features, basic tasks like background removal and text-to-video are surprisingly accessible to beginners.',
    rating: 4.4,
  },
  {
    id: '3',
    name: 'Midjourney',
    description: 'Text-to-image generation for creative visuals',
    bestFor: 'Creators',
    pricing: 'Paid',
    ease: 'Beginner',
    category: 'Design',
    url: 'https://midjourney.com',
    trending: true,
    featured: true,
    tags: ['image-generation', 'art', 'design'],
    useCases: [
      'Create hero images for blog posts and landing pages',
      'Generate product mockups and concept art',
      'Design social media graphics with consistent brand style',
      'Visualise interior design ideas before committing',
    ],
    longDescription:
      'Midjourney is one of the most popular AI image generators, known for producing highly aesthetic, artistic, and photorealistic images from text prompts. It operates primarily through Discord, where users type prompts and receive four image variations within seconds. Midjourney excels at creating illustrations, concept art, product photography, architectural visualisations, and stylised graphics. Its latest versions offer exceptional control over composition, lighting, and style, making it a go-to tool for designers, marketers, and content creators who need high-quality visuals without hiring a photographer or illustrator.',
    rating: 4.6,
  },
  {
    id: '4',
    name: 'Cursor',
    description: 'AI-first code editor for faster development',
    bestFor: 'Developers',
    pricing: 'Freemium',
    ease: 'Pro',
    category: 'Code',
    url: 'https://cursor.sh',
    trending: true,
    isNew: true,
    featured: true,
    tags: ['coding', 'development', 'ide'],
    useCases: [
      'Autocomplete entire functions while coding in your IDE',
      'Refactor legacy code with AI-powered suggestions',
      'Generate unit tests from existing code',
      'Ask questions about your codebase in natural language',
    ],
    longDescription:
      'Cursor is an AI-native code editor built on top of VS Code that fundamentally changes how developers write software. It offers intelligent autocomplete that predicts entire functions, a chat interface that understands your full codebase, and the ability to make multi-file edits from natural language instructions. Cursor indexes your entire project so it can answer questions about your code, suggest refactors that follow your existing patterns, and generate tests that match your testing conventions. It supports all major programming languages and integrates with your existing VS Code extensions and settings.',
    rating: 4.8,
  },
  {
    id: '5',
    name: 'Descript',
    description: 'Edit audio and video by editing text',
    bestFor: 'Creators',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Audio',
    url: 'https://descript.com',
    isNew: true,
    tags: ['audio-editing', 'video-editing', 'transcription'],
    useCases: [
      'Edit podcast episodes by deleting text in the transcript',
      'Remove filler words and awkward pauses automatically',
      'Create short video clips from long recordings',
      'Add captions and subtitles to videos instantly',
    ],
    longDescription:
      'Descript is a revolutionary audio and video editing tool that lets you edit media by editing text. When you import a recording, Descript automatically transcribes it and creates a text-based timeline — delete a word from the transcript and it disappears from the audio or video. It includes AI-powered features like automatic filler word removal, studio-quality audio enhancement, eye contact correction for video, and the ability to generate realistic voice clones for corrections. Ideal for podcasters, YouTubers, and anyone who creates spoken-word content but finds traditional editing software intimidating.',
    rating: 4.5,
  },
  {
    id: '6',
    name: 'Jasper',
    description: 'AI content platform for marketing teams',
    bestFor: 'Teams',
    pricing: 'Paid',
    ease: 'Beginner',
    category: 'Writing',
    url: 'https://jasper.ai',
    featured: true,
    tags: ['writing', 'marketing', 'content'],
    useCases: [
      'Write SEO-optimised blog posts at scale',
      'Generate ad copy variations for A/B testing',
      'Create product descriptions for e-commerce listings',
      'Draft email marketing campaigns from a brief',
    ],
    longDescription:
      "Jasper is an enterprise-grade AI content platform built specifically for marketing teams. It goes beyond simple text generation by offering brand voice training, campaign workflows, SEO optimisation, and team collaboration features. Jasper can produce blog posts, social media content, ad copy, email campaigns, and product descriptions — all while maintaining your brand's unique tone and style guidelines. It integrates with tools like Surfer SEO, Google Docs, and various CMS platforms, making it a central hub for content operations at scale.",
    rating: 4.3,
  },
  {
    id: '7',
    name: 'Copy.ai',
    description: 'Generate marketing copy and content at scale',
    bestFor: 'Solo operators',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Writing',
    url: 'https://copy.ai',
    tags: ['writing', 'marketing', 'copywriting'],
    useCases: [
      'Generate LinkedIn posts from article summaries',
      'Write product launch announcements in multiple tones',
      'Create cold outreach email sequences',
      'Draft FAQ sections from product documentation',
    ],
    longDescription:
      'Copy.ai is a versatile AI writing platform focused on helping marketers and sales teams generate high-quality copy quickly. It offers specialised workflows for creating blog posts, social media content, product descriptions, email sequences, and sales outreach messages. The platform includes a library of templates and frameworks based on proven copywriting formulas, making it easy to produce persuasive content even without marketing expertise. Copy.ai also features a workflow automation engine that can chain multiple AI tasks together for end-to-end content production.',
    rating: 4.4,
  },
  {
    id: '8',
    name: 'Gamma',
    description: 'Create presentations with AI assistance',
    bestFor: 'Teams',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Design',
    url: 'https://gamma.app',
    isNew: true,
    tags: ['presentations', 'design', 'slides'],
    useCases: [
      'Turn meeting notes into polished slide decks',
      'Create investor pitch decks from a one-page brief',
      'Build interactive training materials for onboarding',
      'Generate visual reports from raw data',
    ],
    longDescription:
      'Gamma is an AI-powered presentation tool that transforms the way you create slides, documents, and web pages. Instead of wrestling with layout and design, you provide your content — bullet points, notes, or a brief — and Gamma generates a polished, visually appealing presentation in seconds. It supports interactive elements like embedded videos, charts, and live websites within slides. Gamma presentations are web-native, meaning they look great on any device without compatibility issues, and can be shared as links rather than file attachments.',
    rating: 4.7,
  },
  {
    id: '9',
    name: 'Otter.ai',
    description: 'Real-time transcription and meeting notes',
    bestFor: 'Teams',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Audio',
    url: 'https://otter.ai',
    featured: true,
    tags: ['transcription', 'meetings', 'notes'],
    useCases: [
      'Auto-transcribe Zoom meetings with speaker labels',
      'Search past meetings for specific topics or decisions',
      'Share meeting summaries with absent team members',
      'Generate follow-up action items from call recordings',
    ],
    longDescription:
      'Otter.ai is an AI meeting assistant that automatically joins your Zoom, Google Meet, and Microsoft Teams calls to record, transcribe, and summarise conversations in real time. It identifies different speakers, highlights key points, and generates structured meeting summaries with action items. After meetings, you can search across your entire library of transcripts to find specific discussions, decisions, or commitments. Otter integrates with calendar apps to automatically join scheduled meetings, making it a set-and-forget solution for teams who want to capture every important conversation.',
    rating: 4.5,
  },
  {
    id: '10',
    name: 'Canva AI',
    description: 'Design platform with AI-powered tools',
    bestFor: 'Solo operators',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Design',
    url: 'https://canva.com',
    tags: ['design', 'image-generation', 'templates'],
    useCases: [
      'Generate social media templates matching your brand',
      'Remove and replace photo backgrounds instantly',
      'Create presentation slides from text prompts',
      'Resize designs for every platform in one click',
    ],
    longDescription:
      "Canva AI brings artificial intelligence into the world's most popular design platform, making professional-quality design accessible to everyone. Its AI features include Magic Design (generate complete designs from a text prompt), Magic Eraser (remove unwanted objects from photos), Background Remover, text-to-image generation, and Magic Write for generating copy. With thousands of templates, a massive asset library, and one-click resizing for different platforms, Canva AI is the go-to tool for non-designers who need to create social media graphics, presentations, posters, videos, and marketing materials.",
    rating: 4.7,
  },
  {
    id: '11',
    name: 'ElevenLabs',
    description: 'AI voice generation and cloning',
    bestFor: 'Creators',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Audio',
    url: 'https://elevenlabs.io',
    isNew: true,
    tags: ['voice-generation', 'voice-cloning', 'audio'],
    useCases: [
      'Create voiceovers for YouTube videos without recording',
      'Clone your voice for consistent podcast intros',
      'Generate multilingual audio for global content',
      'Add natural-sounding narration to e-learning courses',
    ],
    longDescription:
      'ElevenLabs is the leading AI voice technology platform, offering remarkably natural-sounding text-to-speech, voice cloning, and audio generation. You can create voiceovers in dozens of languages with voices that convey genuine emotion and natural cadence — far beyond the robotic sound of traditional TTS. The voice cloning feature lets you replicate any voice from just a few minutes of sample audio, enabling consistent branding across content. ElevenLabs is widely used by podcasters, YouTubers, audiobook producers, game developers, and e-learning creators who need professional voice content without recording sessions.',
    rating: 4.6,
  },
  {
    id: '12',
    name: 'Perplexity',
    description: 'AI-powered search and research assistant',
    bestFor: 'Solo operators',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Research',
    url: 'https://perplexity.ai',
    featured: true,
    tags: ['research', 'search', 'analysis'],
    useCases: [
      'Research competitors with cited, up-to-date sources',
      'Get quick answers with links to verify claims',
      'Summarise academic papers with source references',
      'Compare products or services with real-time data',
    ],
    longDescription:
      'Perplexity is an AI-powered search engine that combines the conversational interface of a chatbot with the reliability of cited sources. Unlike traditional search engines that return a list of links, Perplexity reads and synthesises information from multiple sources to provide direct, comprehensive answers with inline citations you can verify. It excels at research tasks, competitive analysis, fact-checking, and staying current on rapidly evolving topics. The Pro tier offers access to more powerful models and the ability to upload files for analysis, making it a serious research tool for professionals and academics.',
    rating: 4.8,
  },
  {
    id: 'higgsfield',
    name: 'Higgsfield',
    description:
      'AI-powered video creation tool that generates realistic human videos and animations from text prompts',
    bestFor: 'Creators',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Video',
    url: 'https://higgsfield.ai',
    isNew: true,
    tags: ['video-generation', 'animation', 'avatars'],
    useCases: [
      'Create talking-head videos without filming yourself',
      'Generate animated explainer clips from scripts',
      'Produce personalised video messages at scale',
      'Create social media video content from text prompts',
    ],
    longDescription:
      "Higgsfield is an AI video creation platform specialising in generating realistic human videos and animations from text prompts. It can create talking-head videos, animated characters, and personalised video messages without any filming equipment or acting skills. The platform is particularly useful for social media creators, marketers, and educators who need video content at scale but lack the time or resources for traditional video production. Higgsfield's AI handles lip-syncing, facial expressions, and natural body movement, producing results that feel authentic and engaging.",
    rating: 4.1,
  },
  {
    id: 'gemini',
    name: 'Google Gemini',
    description:
      "Google's AI assistant for writing, research, coding, and creative tasks — powered by the Gemini model family",
    bestFor: 'Solo operators',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Design',
    url: 'https://gemini.google.com',
    featured: true,
    trending: true,
    tags: ['writing', 'image-generation', 'coding', 'research'],
    useCases: [
      'Analyse images and documents with multimodal understanding',
      'Draft long-form content with research assistance',
      'Generate code snippets across multiple languages',
      'Summarise YouTube videos and extract key points',
    ],
    longDescription:
      "Google Gemini is a multimodal AI assistant that can understand and generate text, images, code, and more. Built on Google's most advanced AI models, it has access to real-time information through Google Search, can analyse images and documents you upload, and integrates deeply with Google Workspace apps like Gmail, Docs, and Drive. Gemini excels at research tasks, creative writing, coding assistance, and visual understanding. Its free tier is generous, and the Advanced tier unlocks longer context windows, more capable models, and deeper Google Workspace integration.",
    rating: 4.3,
  },
  {
    id: 'imagefx',
    name: 'ImageFX',
    description:
      "Google's AI image generator that creates high-quality images from text descriptions using the Imagen model",
    bestFor: 'Creators',
    pricing: 'Free',
    ease: 'Beginner',
    category: 'Design',
    url: 'https://aitestkitchen.withgoogle.com/tools/image-fx',
    isNew: true,
    tags: ['image-generation', 'art', 'design'],
    useCases: [
      'Generate royalty-free images for blog posts',
      'Create custom illustrations for presentations',
      'Design social media visuals from text descriptions',
      'Prototype visual concepts before hiring a designer',
    ],
    longDescription:
      "ImageFX is Google's free AI image generator, powered by the Imagen model family. It creates high-quality, photorealistic and artistic images from text descriptions with an intuitive interface that suggests creative modifications to your prompts. ImageFX stands out for its ease of use — there are no complex settings or parameters to learn — and its output quality rivals paid alternatives. It's particularly well-suited for creating blog illustrations, social media visuals, concept art, and presentation graphics. Being completely free makes it an excellent starting point for anyone exploring AI image generation.",
    rating: 4.2,
  },
  {
    id: 'musicfx',
    name: 'MusicFX',
    description:
      "Google's AI music generator that creates original tracks and soundscapes from text prompts",
    bestFor: 'Creators',
    pricing: 'Free',
    ease: 'Beginner',
    category: 'Audio',
    url: 'https://aitestkitchen.withgoogle.com/tools/music-fx',
    isNew: true,
    tags: ['music-generation', 'audio', 'soundscapes'],
    useCases: [
      'Create background music for YouTube videos',
      'Generate ambient soundscapes for focus or meditation',
      'Produce jingles and intros for podcasts',
      'Experiment with musical ideas before studio sessions',
    ],
    longDescription:
      "MusicFX is Google's free AI music generator that creates original tracks and soundscapes from simple text descriptions. Describe the mood, genre, instruments, and tempo you want, and MusicFX produces a unique audio clip in seconds. It's designed for creators who need background music for videos, podcasts, presentations, or personal projects without dealing with licensing fees or music production software. While it's not meant to replace professional music production, it's an excellent tool for generating ambient tracks, jingles, intros, and atmospheric soundscapes.",
    rating: 4.0,
  },
  {
    id: 'textfx',
    name: 'TextFX',
    description:
      "Google's creative writing tool with AI-powered prompts for wordplay, acronyms, similes, and more",
    bestFor: 'Creators',
    pricing: 'Free',
    ease: 'Beginner',
    category: 'Writing',
    url: 'https://aitestkitchen.withgoogle.com/tools/text-fx',
    isNew: true,
    tags: ['writing', 'wordplay', 'creative-writing'],
    useCases: [
      'Generate creative wordplay for marketing headlines',
      'Find unexpected metaphors and similes for writing',
      'Create acronyms for project or product names',
      "Break through writer's block with creative prompts",
    ],
    longDescription:
      'TextFX is a free creative writing tool from Google, developed in collaboration with rapper Lupe Fiasco. It offers a collection of AI-powered writing experiments designed to spark creativity — tools for generating acronyms, finding unexpected connections between concepts, creating similes and metaphors, exploring semantic relationships, and playing with language in novel ways. Unlike general-purpose AI writers, TextFX is specifically designed to help you think differently about words and language, making it ideal for copywriters, poets, lyricists, and anyone looking to break through creative blocks.',
    rating: 4.1,
  },
  {
    id: 'google-vids',
    name: 'Google Vids',
    description:
      "Google Workspace's AI-powered video creation app for making presentations, training videos, and team updates",
    bestFor: 'Teams',
    pricing: 'Paid',
    ease: 'Beginner',
    category: 'Video',
    url: 'https://workspace.google.com/products/vids',
    tags: ['video-generation', 'presentations', 'training'],
    useCases: [
      'Create team update videos from bullet points',
      'Build training walkthroughs for new employees',
      'Turn quarterly reports into visual presentations',
      'Produce product demo videos from scripts',
    ],
    longDescription:
      "Google Vids is a video creation app built into Google Workspace that uses AI to help teams produce professional-looking videos without any video editing experience. You provide a script, bullet points, or document, and Google Vids generates a complete video with relevant stock footage, animations, transitions, and text overlays. It's designed for business use cases like team updates, training materials, product demos, and quarterly reports. Because it's part of Google Workspace, it integrates seamlessly with Drive, Docs, and Slides, and supports real-time collaboration.",
    rating: 3.9,
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    description:
      "OpenAI's AI assistant for conversation, writing, coding, image generation, and creative problem-solving",
    bestFor: 'Solo operators',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Design',
    url: 'https://chat.openai.com',
    featured: true,
    trending: true,
    tags: ['writing', 'image-generation', 'coding', 'conversation', 'research'],
    useCases: [
      'Generate social media posts from a content brief',
      'Create custom images for presentations or blog posts',
      'Write and debug Python scripts for data analysis',
      'Get instant answers to complex research questions',
    ],
    longDescription:
      "ChatGPT by OpenAI is the world's most widely used AI assistant, capable of conversation, writing, coding, image generation, data analysis, and creative problem-solving. It supports file uploads, web browsing, code execution, and DALL-E image generation all within a single interface. ChatGPT's strength lies in its versatility — it can draft emails, write code, analyse spreadsheets, create images, summarise documents, and answer complex questions across virtually any domain. The free tier provides access to GPT-4o mini, while Plus subscribers get priority access to the latest models, more image generations, and advanced features like custom GPTs.",
    rating: 4.8,
  },
  {
    id: 'grok',
    name: 'Grok',
    description:
      "xAI's conversational AI assistant with real-time access to information and a witty, direct communication style",
    bestFor: 'Solo operators',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Design',
    url: 'https://grok.x.ai',
    trending: true,
    isNew: true,
    tags: ['conversation', 'research', 'image-generation'],
    useCases: [
      'Get real-time analysis of trending topics and news',
      'Ask questions with access to live X/Twitter data',
      'Generate images for social media posts',
      'Research current events with up-to-the-minute context',
    ],
    longDescription:
      "Grok is xAI's conversational AI assistant, distinguished by its real-time access to X (formerly Twitter) data and a direct, sometimes witty communication style. It can analyse trending topics, provide up-to-the-minute context on current events, generate images, and engage in wide-ranging conversations. Grok's unique advantage is its integration with the X platform, giving it access to real-time public discourse and breaking news that other AI assistants may not have. It's available to X Premium subscribers and offers both a standard mode and a more unfiltered \"fun mode\" for casual interactions.",
    rating: 4.2,
  },
  {
    id: 'reclaim-ai',
    name: 'Reclaim.ai',
    description:
      'AI-powered smart scheduling tool that automatically finds the best time for tasks, habits, and meetings on your calendar',
    bestFor: 'Teams',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Research',
    url: 'https://reclaim.ai',
    isNew: true,
    tags: ['scheduling', 'calendar', 'productivity'],
    useCases: [
      'Auto-schedule focus time blocks around meetings',
      'Protect personal habits like lunch and exercise',
      'Find optimal meeting times across team calendars',
      'Automatically reschedule tasks when priorities shift',
    ],
    longDescription:
      "Reclaim.ai is an AI-powered smart scheduling tool that automatically optimises your calendar by finding the best times for tasks, habits, meetings, and breaks. It integrates with Google Calendar and understands your priorities, deadlines, and preferences to dynamically schedule and reschedule your time as things change throughout the week. Reclaim protects personal habits like lunch breaks and exercise, automatically finds meeting times that work for everyone, and ensures you have enough focus time for deep work. It's particularly valuable for busy professionals and teams who struggle with calendar overload and context switching.",
    rating: 4.6,
  },
  {
    id: 'fireflies-ai',
    name: 'Fireflies.ai',
    description:
      'AI meeting assistant that automatically records, transcribes, and summarises your meetings with searchable notes',
    bestFor: 'Teams',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Audio',
    url: 'https://fireflies.ai',
    featured: true,
    isNew: true,
    tags: ['transcription', 'meetings', 'summaries'],
    useCases: [
      'Auto-record and transcribe every meeting',
      'Search across all past meetings by keyword',
      'Generate meeting summaries and share with stakeholders',
      'Track action items and follow-ups automatically',
    ],
    longDescription:
      'Fireflies.ai is an AI meeting assistant that automatically records, transcribes, and summarises your meetings across Zoom, Google Meet, Microsoft Teams, and other platforms. It uses AI to identify speakers, extract action items, highlight key decisions, and generate structured meeting summaries that can be shared with your team. The platform includes a powerful search feature that lets you find specific moments across your entire meeting history by keyword, topic, or speaker. Fireflies integrates with CRMs, project management tools, and collaboration platforms, making it easy to push meeting insights directly into your existing workflows.',
    rating: 4.5,
  },
  {
    id: 'adobe-acrobat-ai',
    name: 'Adobe Acrobat AI Assistant',
    description:
      'AI-powered assistant built into Adobe Acrobat that summarises, answers questions about, and generates content from your PDFs',
    bestFor: 'Teams',
    pricing: 'Paid',
    ease: 'Beginner',
    category: 'Research',
    url: 'https://www.adobe.com/acrobat/generative-ai-pdf.html',
    featured: true,
    tags: ['documents', 'pdf', 'research', 'summaries'],
    useCases: [
      'Summarise 50-page contracts into key points',
      'Ask questions about complex legal or technical PDFs',
      'Generate formatted summaries from research papers',
      'Extract specific data points from lengthy reports',
    ],
    longDescription:
      "Adobe Acrobat AI Assistant is an AI-powered feature built directly into Adobe Acrobat that helps you understand, summarise, and extract information from PDF documents. It can summarise lengthy contracts, research papers, and reports into concise overviews, answer specific questions about document content, and generate formatted summaries with key points and citations. The AI understands complex document structures including tables, charts, and multi-column layouts. It's particularly valuable for legal professionals, researchers, and business users who regularly work with dense, lengthy PDF documents and need to extract insights quickly.",
    rating: 4.4,
  },
  {
    id: 'asana-ai',
    name: 'Asana AI',
    description:
      'AI-powered project management features that help teams set goals, assign tasks, and track progress with smart recommendations',
    bestFor: 'Teams',
    pricing: 'Paid',
    ease: 'Beginner',
    category: 'Research',
    url: 'https://asana.com/product/ai',
    isNew: true,
    tags: ['project-management', 'tasks', 'productivity'],
    useCases: [
      'Auto-generate project plans from a goal description',
      'Get smart task recommendations based on team workload',
      'Summarise project status for stakeholder updates',
      'Identify at-risk tasks and suggest timeline adjustments',
    ],
    longDescription:
      "Asana AI brings artificial intelligence into one of the most popular project management platforms, helping teams plan, execute, and track work more efficiently. It can auto-generate project plans from a simple goal description, recommend task assignments based on team capacity and expertise, summarise project status for stakeholder updates, and identify at-risk tasks before they become blockers. Asana AI understands the context of your projects, team structure, and historical patterns, making its recommendations increasingly accurate over time. It's designed for teams who want to spend less time on project administration and more time on meaningful work.",
    rating: 4.3,
  },
  {
    id: 'hubspot-ai',
    name: 'HubSpot AI',
    description:
      'AI tools built into HubSpot CRM for generating marketing content, automating outreach, and analysing customer data',
    bestFor: 'Teams',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Writing',
    url: 'https://www.hubspot.com/artificial-intelligence',
    featured: true,
    tags: ['marketing', 'crm', 'writing', 'automation'],
    useCases: [
      'Generate personalised email sequences for leads',
      'Create blog post drafts optimised for SEO',
      'Analyse customer data to identify sales opportunities',
      'Auto-draft social media posts from campaign briefs',
    ],
    longDescription:
      "HubSpot AI integrates artificial intelligence throughout the HubSpot CRM platform, enhancing marketing, sales, and customer service workflows. It can generate personalised email sequences, create SEO-optimised blog content, analyse customer data to identify sales opportunities, draft social media posts, and automate routine outreach tasks. Because it's built into HubSpot's CRM, the AI has access to your customer data, interaction history, and pipeline information, enabling highly personalised and contextually relevant outputs. It's ideal for marketing and sales teams who want to scale their efforts without proportionally scaling their headcount.",
    rating: 4.4,
  },
  {
    id: 'superhuman',
    name: 'Superhuman',
    description:
      'AI-powered email client that helps you write, triage, and respond to emails faster with smart drafts and instant summaries',
    bestFor: 'Solo operators',
    pricing: 'Paid',
    ease: 'Beginner',
    category: 'Writing',
    url: 'https://superhuman.com',
    trending: true,
    tags: ['email', 'writing', 'productivity'],
    useCases: [
      'Triage your inbox with AI-powered priority sorting',
      'Generate reply drafts that match your writing style',
      'Summarise long email threads into key points',
      'Schedule follow-ups automatically based on context',
    ],
    longDescription:
      "Superhuman is a premium AI-powered email client designed to make you the fastest, most productive email user possible. Its AI features include smart triage that prioritises your inbox, instant email summaries for long threads, AI-generated reply drafts that match your personal writing style, and automated follow-up scheduling. Beyond AI, Superhuman offers keyboard shortcuts for every action, split inboxes for organising different types of email, read statuses, and undo send. It's built for professionals who spend significant time in email and want to cut that time in half while maintaining high-quality communication.",
    rating: 4.7,
  },
  {
    id: 'zapier-ai',
    name: 'Zapier AI',
    description:
      'AI-powered automation platform that connects your apps and builds custom workflows, chatbots, and AI-assisted tasks without code',
    bestFor: 'Teams',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Research',
    url: 'https://zapier.com/ai',
    isNew: true,
    tags: ['automation', 'workflows', 'productivity'],
    useCases: [
      'Build automated workflows between apps using plain English',
      'Create AI chatbots for customer support without code',
      'Auto-sort incoming leads into CRM categories',
      'Generate weekly reports from multiple data sources',
    ],
    longDescription:
      'Zapier AI extends the world\'s most popular automation platform with artificial intelligence capabilities, letting you build sophisticated workflows using plain English instead of complex logic. You can describe what you want to automate and Zapier AI will create the workflow for you, connecting any combination of 6,000+ apps. It also offers AI-powered chatbots, data extraction, text analysis, and content generation as steps within your automations. This means you can build workflows like "when a new lead fills out a form, use AI to qualify them, enrich their data, and route them to the right sales rep" — all without writing code.',
    rating: 4.6,
  },
  {
    id: 'typeform-ai',
    name: 'Typeform AI',
    description:
      'AI-powered form and survey builder that generates conversational forms, analyses responses, and creates engaging experiences in seconds',
    bestFor: 'Teams',
    pricing: 'Paid',
    ease: 'Beginner',
    category: 'Design',
    url: 'https://www.typeform.com',
    isNew: true,
    tags: ['forms', 'surveys', 'data-collection'],
    useCases: [
      'Generate engaging survey forms from a topic description',
      'Create lead capture forms that feel conversational',
      'Build quiz-style assessments for training',
      'Analyse form responses with AI-powered insights',
    ],
    longDescription:
      'Typeform AI enhances the popular form and survey platform with artificial intelligence that can generate entire forms from a simple description, create conversational experiences that adapt based on responses, and analyse submission data with AI-powered insights. The platform is known for its beautiful, one-question-at-a-time interface that achieves significantly higher completion rates than traditional forms. With AI, you can create lead capture forms, customer surveys, quizzes, and assessments in seconds, then let the AI analyse patterns in your responses and suggest improvements to increase engagement.',
    rating: 4.5,
  },
  {
    id: 'zendesk-ai',
    name: 'Zendesk AI',
    description:
      'AI-powered customer service platform that automates ticket routing, suggests replies, and resolves common support requests instantly',
    bestFor: 'Teams',
    pricing: 'Paid',
    ease: 'Pro',
    category: 'Writing',
    url: 'https://www.zendesk.com/ai',
    isNew: true,
    tags: ['customer-support', 'automation', 'crm'],
    useCases: [
      'Auto-route support tickets to the right team',
      'Suggest reply templates based on ticket content',
      'Resolve common customer questions with AI agents',
      'Analyse support trends to improve documentation',
    ],
    longDescription:
      "Zendesk AI brings artificial intelligence to customer service operations, automating ticket routing, suggesting agent replies, resolving common questions with AI agents, and analysing support trends to improve your documentation and processes. It's trained on billions of real customer service interactions, so it understands the nuances of support conversations out of the box. Zendesk AI can handle routine inquiries autonomously, freeing human agents to focus on complex issues that require empathy and judgment. For support teams handling high ticket volumes, it can dramatically reduce response times and improve customer satisfaction scores.",
    rating: 4.2,
  },
  {
    id: 'coda-ai',
    name: 'Coda AI',
    description:
      'AI assistant built into Coda docs that helps draft content, summarise tables, generate formulas, and automate workflows in your workspace',
    bestFor: 'Teams',
    pricing: 'Freemium',
    ease: 'Beginner',
    category: 'Writing',
    url: 'https://coda.io/product/ai',
    isNew: true,
    tags: ['documents', 'workspace', 'productivity'],
    useCases: [
      'Draft meeting agendas from previous notes',
      'Generate formulas for complex table calculations',
      'Summarise long documents into executive briefs',
      'Auto-fill tables with AI-generated content',
    ],
    longDescription:
      "Coda AI is an intelligent assistant built into the Coda document platform that combines the flexibility of documents, spreadsheets, and applications in one tool. It can draft content, summarise tables, generate complex formulas, auto-fill columns with AI-generated data, and create entire document templates from descriptions. Because Coda AI understands the structure of your docs — including tables, databases, and automations — it can perform tasks that go beyond simple text generation, like populating a project tracker or creating a meeting agenda based on your team's recurring patterns. It's ideal for teams who use Coda as their central workspace.",
    rating: 4.4,
  },
  {
    id: 'clickup-ai',
    name: 'ClickUp AI',
    description:
      'AI-powered project management assistant that drafts documents, summarises tasks, generates action items, and automates team workflows',
    bestFor: 'Teams',
    pricing: 'Paid',
    ease: 'Beginner',
    category: 'Research',
    url: 'https://clickup.com/ai',
    isNew: true,
    tags: ['project-management', 'tasks', 'productivity'],
    useCases: [
      'Generate project briefs from a one-line description',
      'Summarise task comments into key decisions',
      'Draft status update emails from project data',
      'Create subtask breakdowns for complex deliverables',
    ],
    longDescription:
      "ClickUp AI is an AI assistant integrated throughout the ClickUp project management platform, designed to help teams work faster at every stage of project execution. It can generate project briefs from a single sentence, break complex tasks into detailed subtasks, summarise comment threads into key decisions, draft status update emails from project data, and create documentation from scratch. ClickUp AI understands your project hierarchy, team assignments, and deadlines, so its outputs are contextually relevant to your actual work. It's particularly useful for project managers and team leads who spend too much time on administrative writing and status reporting.",
    rating: 4.3,
  },
]

export const taskSuggestions = [
  'Write a proposal',
  'Summarise a meeting',
  'Remove a background',
  'Build a landing page',
  'Generate a logo',
  'Transcribe audio',
  'Automate emails',
  'Edit a video',
]
